import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Clock, HeartPulse, TrendingDown, TrendingUp } from "lucide-react";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, fmtDate } from "@/lib/api";
import { Loader, MODULES, Reveal, Stat, TONES } from "@/components/common";
import { StreakCard } from "@/components/StreakCard";
import { MedalsRow } from "@/components/Medals";
import { toast } from "sonner";
import { ZONE_LABELS } from "@/components/AilmentForm";

const ModuleCard = ({ module, children, testid, delay }) => {
  const m = MODULES[module];
  return (
    <Reveal delay={delay} className="min-w-0">
      <Link to={m.path} data-testid={testid} className={`group block h-full rounded-3xl ${TONES[m.tone].soft} p-6 transition-transform duration-300 hover:scale-[1.015]`}>
        <div className="flex items-center justify-between">
          <span className={`flex items-center gap-2 text-sm font-bold ${TONES[m.tone].text}`}><m.icon className="h-4 w-4" /> {m.label}</span>
          <ArrowUpRight className={`h-5 w-5 ${TONES[m.tone].text} transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5`} />
        </div>
        {children}
      </Link>
    </Reveal>
  );
};

const TodayWorkout = ({ day }) => {
  const w = day.workout;
  const done = day.feedback?.completed;
  return (
    <div className="mt-4">
      <p className="font-display text-xl font-extrabold text-ink" data-testid="dashboard-workout-title">{w.title}</p>
      <p className="mt-1 flex items-center gap-1.5 text-sm text-stone-600"><Clock className="h-4 w-4" /> {w.minutes} min · {w.items.length} ejercicios</p>
      {w.items.some((i) => i.substituted_from) && <p className="mt-3 inline-block rounded-full bg-white px-3 py-1 text-xs font-semibold text-care-ink">Adaptado a tus dolencias</p>}
      <p className={`mt-4 flex items-center gap-2 text-sm font-semibold ${done ? "text-food-ink" : "text-train-ink"}`} data-testid="dashboard-workout-status">
        {done ? <><CheckCircle2 className="h-4 w-4" /> Completado</> : "Pendiente · toca para empezar"}
      </p>
    </div>
  );
};

const TodayMeals = ({ day }) => {
  const eaten = day.meals.filter((m) => day.meals_done.includes(m.slot)).reduce((s, m) => s + m.kcal, 0);
  const pct = Math.min(100, Math.round((eaten / day.kcal_target) * 100));
  return (
    <div className="mt-4">
      <p className="font-display text-xl font-extrabold text-ink" data-testid="dashboard-kcal">{eaten} <span className="text-sm font-semibold text-stone-500">/ {day.kcal_target} kcal</span></p>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-white"><div className="h-full rounded-full bg-food transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      <ul className="mt-4 space-y-1.5">
        {day.meals.map((m) => (
          <li key={m.slot} className="flex items-center gap-2 text-sm">
            <CheckCircle2 className={`h-4 w-4 shrink-0 ${day.meals_done.includes(m.slot) ? "text-food" : "text-stone-300"}`} />
            <span className="font-semibold">{m.label}:</span><span className="truncate text-stone-600">{m.name}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AilmentStatus = ({ ailments }) => (
  <div className="mt-4 space-y-3">
    {!ailments.length && <p className="text-sm text-stone-600">Sin dolencias activas. ¡Genial!</p>}
    {ailments.slice(0, 3).map((a) => (
      <div key={a.id} className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
        <div className="min-w-0"><p className="truncate text-sm font-semibold">{a.name}</p><p className="text-xs text-stone-500">{ZONE_LABELS[a.zone]} · tendencia: {a.advice.trend}</p></div>
        <span className="font-display text-lg font-extrabold text-care-ink">{a.intensity}<span className="text-xs">/10</span></span>
      </div>
    ))}
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/dashboard").then((r) => {
      setD(r.data);
      r.data.new_medals.forEach((m) => toast.success(`¡Nueva medalla: ${m.name}! ${m.days} días seguidos`));
    });
  }, []);
  if (!d) return <Loader />;
  const diff = +(d.stats.weight_current - d.stats.weight_start).toFixed(1);
  const hour = new Date().getHours();
  const hello = hour < 13 ? "Buenos días" : hour < 20 ? "Buenas tardes" : "Buenas noches";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <Reveal>
        <p className="eyebrow text-stone-500">Triactiva · {fmtDate(d.today.date)}</p>
        <h1 className="mt-2 text-3xl font-extrabold sm:text-5xl" data-testid="dashboard-greeting">{hello}, {d.name}</h1>
        <p className="mt-2 text-stone-600">Esto es lo que toca hoy.</p>
      </Reveal>
      <Reveal delay={0.05}><StreakCard streak={d.streak} /></Reveal>
      <Reveal delay={0.08}><MedalsRow streak={d.streak} /></Reveal>
      <div className="grid gap-4 md:grid-cols-2">
        {d.modules.training && d.today.workout && <ModuleCard module="training" testid="dashboard-training-card" delay={0.1}><TodayWorkout day={d.today} /></ModuleCard>}
        {d.modules.nutrition && d.today.meals && <ModuleCard module="nutrition" testid="dashboard-nutrition-card" delay={0.15}><TodayMeals day={d.today} /></ModuleCard>}
        {d.modules.ailments && <ModuleCard module="ailments" testid="dashboard-ailments-card" delay={0.2}><AilmentStatus ailments={d.ailments} /></ModuleCard>}
        <Reveal delay={0.25} className="min-w-0">
          <Link to="/progreso" data-testid="dashboard-progress-card" className="card-clay block h-full p-6 transition-transform duration-300 hover:scale-[1.015]">
            <div className="flex items-center justify-between"><span className="text-sm font-bold text-streak-ink">Tu progreso</span><ArrowUpRight className="h-5 w-5 text-streak-ink" /></div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <Stat label="Peso" value={d.stats.weight_current} unit="kg" testid="dashboard-weight" />
              <Stat label="Cambio" value={<span className="inline-flex items-center gap-1">{diff <= 0 ? <TrendingDown className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}{diff}</span>} unit="kg" testid="dashboard-weight-diff" />
              <Stat label="Min. activos 7d" value={d.active_minutes_week} testid="dashboard-active-minutes" />
            </div>
            {d.weights.length > 1 && (
              <div className="mt-4 h-24">
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 96 }}>
                  <LineChart data={d.weights}><XAxis dataKey="date" hide /><YAxis hide domain={["dataMin - 1", "dataMax + 1"]} /><Tooltip formatter={(v) => `${v} kg`} labelFormatter={fmtDate} />
                    <Line type="monotone" dataKey="weight_kg" stroke="#F59E0B" strokeWidth={3} dot={false} /></LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Link>
        </Reveal>
      </div>
      {d.tomorrow?.workout && d.modules.training && (
        <Reveal delay={0.3} className="flex items-center gap-3 rounded-2xl border border-dashed border-stone-300 px-5 py-4 text-sm text-stone-600">
          <HeartPulse className="h-4 w-4 text-train" /> Mañana: <strong className="text-ink">{d.tomorrow.workout.title}</strong>
        </Reveal>
      )}
    </div>
  );
}
